import { Injectable } from "@angular/core";
import { INgsiMapBaseEntity } from "./interfaces/ngsi-entity.interface";
import { IMapWidgetFormGroupSettings } from "../../map-widget-settings/map-widget-settings.interface";
import { ILeafletInstance } from "../leaflet/leaflet.interface";
import * as NgsildConstants from "./helpers/ngsild.constants";
import { INgsiAIS } from "./interfaces/ngsi-ais.interface";
import { MarkerService } from "../leaflet/markers/marker.service";
import { PolygonService } from "../leaflet/polygons/polygon.service";
import L from "../leaflet/leaflet-plugins-loader";
import { CommonMapHelperService } from "../leaflet/helper/common-map-helper.service";
import { PolylineService } from "../leaflet/polylines/polyline.service";
import * as leafletInstanceConfig from "../ngsild/helpers/leaflet-instance-config.constants";

@Injectable({
  providedIn: "root",
})
export class DataAdapter {
  protected constructor(
    private readonly markerService: MarkerService,
    private readonly polygonService: PolygonService,
    private readonly polylineService: PolylineService,
  ) {}

  processIncomingData(
    appPluginWidgetId: string,
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
    temporaryData?: boolean,
  ) {
    const { dataModel } = leafletInstanceConfig.config;
    const ignoreEntity = CommonMapHelperService.ignoreIncomingEntity(
      entity,
      instance,
    );

    if (ignoreEntity) {
      return;
    }

    switch (entity.type) {
      case dataModel.AIS.type: {
        const eAIS = entity as INgsiAIS;
        const isTemporaryEntity =
          temporaryData ||
          !NgsildConstants.appPluginWidgetHasEntityId(settings.entities, eAIS);

        this.markerService.addOrUpdateMarker(
          appPluginWidgetId,
          entity,
          settings,
          instance,
          isTemporaryEntity,
        );

        if (!isTemporaryEntity) {
          this.addAISMapPanesOverlays(
            appPluginWidgetId,
            settings,
            eAIS,
            instance,
          );
        }
        return;
      }

      case dataModel.MaritimeTargets.type:
      case dataModel.RadarTarget.type:
      case dataModel.FusionMaritimeTarget.type:
        this.markerService.addOrUpdateMarker(
          appPluginWidgetId,
          entity,
          settings,
          instance,
          true,
        );
        return;

      case dataModel.PlannedPath.type:
        this.polylineService.addOrUpdatePolyline(
          appPluginWidgetId,
          entity,
          "plannedPath_overlayPane",
          instance,
        );
        return;

      default:
        return;
    }
  }

  private addAISMapPanesOverlays(
    appPluginWidgetId: string,
    settings: IMapWidgetFormGroupSettings,
    entity: INgsiAIS,
    instance: ILeafletInstance,
  ) {
    if (settings?.dataSourceTypeCategory) {
      const addTrackingLine = this.addTrackingLineToMarker(settings, entity);
      const addPredictionLine = this.addPredictionLineToMarker(
        settings,
        entity,
      );

      if (addTrackingLine) {
        this.polygonService.addOrUpdatePolygon(
          appPluginWidgetId,
          entity,
          settings,
          instance,
          "tracking",
        );
      }

      if (addPredictionLine) {
        this.polygonService.addOrUpdatePolygon(
          appPluginWidgetId,
          entity,
          settings,
          instance,
          "prediction",
        );
      }
    }

    this.LoadMarkerOnMiniMap(appPluginWidgetId, instance);
  }

  private addTrackingLineToMarker(
    settings: IMapWidgetFormGroupSettings,
    entity: INgsiAIS,
  ): boolean {
    const trackingEnabled = settings.enableRouteTracking;
    const updateTargetPostion = NgsildConstants.updateVesselTargetPostion(
      entity,
      settings,
    );
    return trackingEnabled && updateTargetPostion;
  }

  private addPredictionLineToMarker(
    settings: IMapWidgetFormGroupSettings,
    entity: INgsiAIS,
  ): boolean {
    const predictionEnabled = settings.enableAisPrediction;
    const updatePredictionLine = NgsildConstants.updatePredictionLine(entity);
    return predictionEnabled && updatePredictionLine;
  }

  private LoadMarkerOnMiniMap(
    appPluginWidgetId: string,
    instance: ILeafletInstance,
  ) {
    const miniMapControl = instance.layerControls.get(
      `minimap_${appPluginWidgetId}`,
    );

    if (!miniMapControl) {
      // console.log('Unknown miniMapControl instance. Markers could not be loaded on minimap.')
      return;
    }

    const miniMap = (miniMapControl as any)._miniMap;

    if (miniMap) {
      instance.markers.forEach((marker: L.Marker<any>, entityId: string) => {
        // Clone the marker position but without interaction (no tooltips/popups)
        const entity = instance.entities.get(entityId);

        if (!entity) {
          // console.log(`Marker will be ignored. EntityId: ${entityId}.`)
          return;
        }

        const markerLatLng = marker.getLatLng();
        const circleMarker = instance.circles.get(`minimap_circle_${entityId}`);

        if (!circleMarker) {
          const markerColor =
            CommonMapHelperService.getMarkerColor(marker) ?? "#fff";

          const newCircleMarker = L.circle(
            [markerLatLng.lat, markerLatLng.lng],
            {
              radius: 5,
              color: markerColor,
              fillColor: markerColor,
              fillOpacity: 0.8,
              weight: 1,
              interactive: false,
            },
          );

          instance.circles.set(`minimap_circle_${entityId}`, newCircleMarker);

          // Add the cloned non-interactive marker to the minimap
          newCircleMarker.addTo(miniMap);
        } else {
          // update circle position on minimap
          circleMarker.setLatLng(markerLatLng);
        }
      });
    }
  }

  clearInstanceFromInheritedServices(_appPluginWidgetId: string) {
    // Reserved for future service cleanup
  }
}
